import React, { useEffect, useState } from 'react';
import { autoPostAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { Eye, ArrowRight, Clock } from 'lucide-react';

/**
 * Self-fetching banner that surfaces auto-post renders waiting on a
 * preview decision. Counterpart to the email. Visible inside the app
 * for users who would otherwise miss the notification.
 *
 * Copy is `preview_mode`-aware (v1.6 fix). In `require_approval` mode
 * silence == skip, so "wartet auf deine Freigabe" is accurate. In the
 * default `auto_with_preview` mode silence == post, so the same copy
 * is actively misleading. The user thinks they must approve when in
 * fact the post fires anyway. The banner now distinguishes:
 *   * require_approval  → "Vorschau wartet auf deine Freigabe"
 *   * auto_with_preview → "Vorschau für deinen geplanten Auto-Post"
 *                          + clear "Wird automatisch um <time> gepostet"
 *
 * Polls on mount only (lightweight). The banner stays accurate through
 * a manual page refresh; that's good enough. Users don't sit on the
 * dashboard for hours waiting for renders to land.
 */
export default function PendingPreviewBanner() {
  const { t, lang } = useTranslation();
  const [pending, setPending] = useState([]);
  const [previewMode, setPreviewMode] = useState('auto_with_preview');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await autoPostAPI.getPendingPreviews();
        if (cancelled) return;
        setPending(res.data?.pending || []);
        // Backend returns the user's preview_mode so we can pick the
        // right copy. Default to auto_with_preview if missing. That's
        // the default for new accounts and the safer fallback (does NOT
        // imply "you must approve" if we don't know).
        setPreviewMode(res.data?.preview_mode || 'auto_with_preview');
      } catch (_) {
        // Silent. Non-critical. 401 redirects via global interceptor.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || pending.length === 0) return null;

  const first = pending[0];
  const count = pending.length;
  const isApprovalRequired = previewMode === 'require_approval';
  // Format the scheduled time in the user's locale. Agents read times
  // locally, not in UTC. Best-effort; if `Date` parsing fails fall back
  // to the raw string.
  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(lang === 'de' ? 'de-AT' : 'en-US', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return iso;
    }
  };

  // Headline + subhead diverge by mode. require_approval keeps the
  // existing "wartet auf Freigabe" because that IS accurate there.
  // auto_with_preview gets "ready / scheduled" copy that makes clear
  // the post fires on its own. The user only acts if they want to
  // adjust or stop it.
  let headline;
  let subhead;
  let cta;
  if (isApprovalRequired) {
    headline =
      count === 1
        ? (t('pendingPreview.singleTitle') || 'Eine Vorschau wartet auf deine Freigabe')
        : (t('pendingPreview.multiTitle', { count })
            || `${count} Vorschauen warten auf deine Freigabe`);
    subhead =
      count === 1 && first.property_title
        ? `${first.property_title}${first.scheduled_for ? ' · ' + formatTime(first.scheduled_for) : ''}`
        : (t('pendingPreview.multiSubtitle')
            || 'Wir haben deine Auto-Posts gerendert. Schau sie dir an, bevor sie raus gehen.');
    cta = count === 1
      ? (t('pendingPreview.review') || 'Vorschau ansehen')
      : (t('pendingPreview.reviewNext') || 'Nächste ansehen');
  } else {
    headline =
      count === 1
        ? (t('pendingPreview.autoSingleTitle') || 'Vorschau für deinen geplanten Auto-Post')
        : (t('pendingPreview.autoMultiTitle', { count })
            || `${count} Auto-Posts bereit zur Vorschau`);
    if (count === 1 && first.scheduled_for) {
      subhead = first.property_title
        ? `${first.property_title} · ${t('pendingPreview.autoSubtitleScheduled', { time: formatTime(first.scheduled_for) })
            || `Wird automatisch um ${formatTime(first.scheduled_for)} gepostet`}`
        : (t('pendingPreview.autoSubtitleScheduled', { time: formatTime(first.scheduled_for) })
            || `Wird automatisch um ${formatTime(first.scheduled_for)} gepostet`);
    } else if (count === 1 && first.property_title) {
      subhead = `${first.property_title} · ${t('pendingPreview.autoSubtitleNoTime')
        || 'Wird automatisch gepostet. Schau rein, wenn du etwas ändern willst.'}`;
    } else {
      subhead = t('pendingPreview.autoMultiSubtitle')
        || 'Werden automatisch gepostet. Reinschauen nur, wenn du etwas ändern oder stoppen willst.';
    }
    cta = count === 1
      ? (t('pendingPreview.autoReview') || 'Anschauen oder stoppen')
      : (t('pendingPreview.autoReviewNext') || 'Nächsten ansehen');
  }

  return (
    <div
      className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-[#1f4d3a]/30 bg-[#e9efe7]"
      data-testid="pending-preview-banner"
    >
      <div className="flex items-start gap-3 min-w-0">
        <Eye className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#2e6b50]">{headline}</p>
          <p className="text-xs text-[#23211a]/80 mt-1 truncate flex items-center gap-1.5">
            {count === 1 && first.scheduled_for && (
              <Clock className="w-3 h-3 shrink-0 inline" />
            )}
            <span className="truncate">{subhead}</span>
          </p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => {
          // Preview pages are public (token-gated), so a normal navigation
          // works regardless of auth state. Direct window.open lets the
          // page open in the same tab. Same flow as clicking from email.
          window.location.href = first.preview_url;
        }}
        className="bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white w-full sm:w-auto sm:shrink-0"
      >
        {cta}
        <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </div>
  );
}
